import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, CaretDown, CaretRight, FileText } from '@phosphor-icons/react';
import { Loader2, X } from 'lucide-react';
import { PhaseSelector } from '../PhaseSelector';

export function ChantiersCSPSFormSheet({
  open, onOpenChange, editingChantier,
  formData, setFormData, submitting, onSubmit,
}) {
  const [newEntreprise, setNewEntreprise] = useState('');
  const [showPgcTrame, setShowPgcTrame] = useState(false);

  const addEntreprise = () => {
    if (newEntreprise.trim() && !formData.entreprises.includes(newEntreprise.trim())) {
      setFormData({ ...formData, entreprises: [...formData.entreprises, newEntreprise.trim()] });
      setNewEntreprise('');
    }
  };

  const removeEntreprise = (name) => {
    setFormData({ ...formData, entreprises: formData.entreprises.filter(e => e !== name) });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" aria-describedby="csps-chantier-form-desc">
        <SheetHeader>
          <SheetTitle>{editingChantier ? 'Modifiér le chantier CSPS' : 'Nouveau chantier CSPS'}</SheetTitle>
          <p id="csps-chantier-form-desc" className="text-sm text-[#4B5563]">
            {editingChantier ? 'Modifiéz les informations' : 'Renseignez les informations du chantier CSPS'}
          </p>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-6 pb-8 overflow-y-auto max-h-[calc(100vh-160px)] max-h-[calc(100dvh-160px)]">
          <div>
            <Label htmlFor="csps-name">Nom du chantier *</Label>
            <Input id="csps-name" value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Construction Ecole Jean Moulin" required
              data-testid="csps-chantier-name" className="mt-1 h-14" />
          </div>

          <div>
            <Label htmlFor="csps-address">Adresse *</Label>
            <Input id="csps-address" value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Ex: 5 avenue de la Republique, 69001 Lyon" required
              data-testid="csps-chantier-address" className="mt-1 h-14" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="csps-mo">Maître d'Ouvrage (MO) *</Label>
              <Input id="csps-mo" value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                placeholder="Ex: Mairie de Lyon" required
                data-testid="csps-chantier-mo" className="mt-1 h-14" />
            </div>
            <div>
              <Label htmlFor="csps-moe">Maître d'Oeuvre (MOE) <span className="text-xs text-gray-400">(optionnel)</span></Label>
              <Input id="csps-moe" value={formData.maitre_oeuvre}
                onChange={(e) => setFormData({ ...formData, maitre_oeuvre: e.target.value })}
                placeholder="Ex: Cabinet Architectes XYZ"
                data-testid="csps-chantier-moe" className="mt-1 h-14" />
            </div>
          </div>

          <div>
            <Label htmlFor="csps-coord">Coordonnateur SPS <span className="text-xs text-gray-400">(optionnel)</span></Label>
            <Input id="csps-coord" value={formData.coordonnateur_sps}
              onChange={(e) => setFormData({ ...formData, coordonnateur_sps: e.target.value })}
              placeholder="Nom du coordonnateur SPS"
              data-testid="csps-coordonnateur" className="mt-1 h-14" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Phase du chantier</Label>
              <PhaseSelector
                value={formData.phase_chantier}
                onChange={(v) => setFormData({ ...formData, phase_chantier: v })}
                testIdPrefix="csps-phase"
              />
            </div>
            <div>
              <Label>Niveau CSPS</Label>
              <Select value={String(formData.niveau_csps)}
                onValueChange={(v) => setFormData({ ...formData, niveau_csps: parseInt(v) })}>
                <SelectTrigger className="mt-1 h-14" data-testid="csps-niveau-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]">
                  <SelectItem value="1" className="py-3">Niveau 1 (gros risques)</SelectItem>
                  <SelectItem value="2" className="py-3">Niveau 2 (risques moyens)</SelectItem>
                  <SelectItem value="3" className="py-3">Niveau 3 (risques faibles)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date de début *</Label>
              <Input type="date" value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required data-testid="csps-start-date" className="mt-1 h-14" />
            </div>
            <div>
              <Label>Date de fin <span className="text-xs text-gray-400">(optionnel)</span></Label>
              <Input type="date" value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                data-testid="csps-end-date" className="mt-1 h-14" />
            </div>
          </div>

          <div>
            <Label>Statut</Label>
            <Select value={formData.status}
              onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger className="mt-1 h-14" data-testid="csps-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="active" className="py-3">Actif</SelectItem>
                <SelectItem value="paused" className="py-3">En pause</SelectItem>
                <SelectItem value="completed" className="py-3">Terminé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Entreprises intervenantes</Label>
            <div className="flex gap-2 mt-1">
              <Input value={newEntreprise}
                onChange={(e) => setNewEntreprise(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEntreprise(); } }}
                placeholder="Nom de l'entreprise"
                data-testid="csps-entreprise-input" className="h-14 flex-1" />
              <Button type="button" onClick={addEntreprise}
                className="h-14 bg-[#FF6B00] hover:bg-orange-700" data-testid="csps-add-entreprise-btn">
                <Plus size={20} />
              </Button>
            </div>
            {formData.entreprises.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.entreprises.map((e, i) => (
                  <span key={`form-ent-${e}-${i}`} className="inline-flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-800 px-2.5 py-1 rounded-md text-sm">
                    {e}
                    <button type="button" onClick={() => removeEntreprise(e)} className="hover:text-red-600">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="pt-3 border-t">
            <p className="text-xs font-semibold text-[#FF6B00] uppercase tracking-wide mb-3">1. Informations travaux</p>
            <div className="space-y-3">
              <div>
                <Label htmlFor="csps-nature">Nature des travaux</Label>
                <Input id="csps-nature" value={formData.nature_travaux}
                  onChange={(e) => setFormData({ ...formData, nature_travaux: e.target.value })}
                  placeholder="Ex: Construction neuve, rehabilitation..."
                  data-testid="csps-nature-travaux" className="mt-1 h-14" />
              </div>
              <div>
                <Label htmlFor="csps-duree">Duree previsionnelle</Label>
                <Input id="csps-duree" value={formData.duree_previsionnelle}
                  onChange={(e) => setFormData({ ...formData, duree_previsionnelle: e.target.value })}
                  placeholder="Ex: 18 mois"
                  data-testid="csps-duree" className="mt-1 h-14" />
              </div>
              <div>
                <Label>Description du chantier</Label>
                <Textarea value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description detaillee de l'operation (surface, niveaux, caracteristiques)..."
                  rows={3} data-testid="csps-description" className="mt-1" />
              </div>
            </div>
          </div>

          {/* TRAME PGC : organisation + securite - optionnelle, pliable */}
          <div className="pt-3 border-t">
            <button
              type="button"
              onClick={() => setShowPgcTrame(!showPgcTrame)}
              className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 transition-colors"
              data-testid="csps-toggle-pgc-trame"
            >
              <div className="flex items-center gap-2">
                <FileText size={20} className="text-purple-700" weight="duotone" />
                <div className="text-left">
                  <p className="text-sm font-bold text-purple-900">Trame PGC (optionnelle)</p>
                  <p className="text-xs text-purple-700">Informations reutilisables dans vos documents PGC</p>
                </div>
              </div>
              {showPgcTrame ? (
                <CaretDown size={20} className="text-purple-700" />
              ) : (
                <CaretRight size={20} className="text-purple-700" />
              )}
            </button>

            {showPgcTrame && (
              <div className="mt-3 space-y-4 pl-1 border-l-2 border-purple-200 ml-3 pl-4 py-2">
                <p className="text-xs italic text-[#6B7280]">
                  Ces champs ne sont pas obligatoires a la creation du chantier. Ils alimentent automatiquement vos trames PGC lorsque vous les utilisez.
                </p>
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Organisation du chantier</p>
                  <div>
                    <Label htmlFor="csps-acces">Acces chantier</Label>
                    <Input id="csps-acces" value={formData.acces_chantier}
                      onChange={(e) => setFormData({ ...formData, acces_chantier: e.target.value })}
                      placeholder="Ex: Portail principal rue de la Paix"
                      data-testid="csps-acces" className="mt-1 h-14" />
                  </div>
                  <div>
                    <Label htmlFor="csps-circulation">Circulation</Label>
                    <Input id="csps-circulation" value={formData.circulation}
                      onChange={(e) => setFormData({ ...formData, circulation: e.target.value })}
                      placeholder="Ex: Sens unique, separation pietons/engins"
                      data-testid="csps-circulation" className="mt-1 h-14" />
                  </div>
                  <div>
                    <Label htmlFor="csps-stockage">Zones de stockage</Label>
                    <Input id="csps-stockage" value={formData.zones_stockage}
                      onChange={(e) => setFormData({ ...formData, zones_stockage: e.target.value })}
                      placeholder="Ex: Zone nord pour materiaux, zone sud pour dechets"
                      data-testid="csps-stockage" className="mt-1 h-14" />
                  </div>
                  <div>
                    <Label htmlFor="csps-installations">Installations de chantier</Label>
                    <Input id="csps-installations" value={formData.installations_chantier}
                      onChange={(e) => setFormData({ ...formData, installations_chantier: e.target.value })}
                      placeholder="Ex: Base vie, sanitaires, refectoire..."
                      data-testid="csps-installations" className="mt-1 h-14" />
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Securite</p>
                  <div>
                    <Label htmlFor="csps-risques">Risques principaux</Label>
                    <Textarea id="csps-risques" value={formData.risques_principaux}
                      onChange={(e) => setFormData({ ...formData, risques_principaux: e.target.value })}
                      placeholder="Ex: Travaux en hauteur, coactivité, risque electrique, manutention mecanique..."
                      rows={3} data-testid="csps-risques" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="csps-contraintes">Contraintes specifiques</Label>
                    <Textarea id="csps-contraintes" value={formData.contraintes_specifiques}
                      onChange={(e) => setFormData({ ...formData, contraintes_specifiques: e.target.value })}
                      placeholder="Ex: Site occupe, proximite ecole, reseaux enterres, zone inondable..."
                      rows={3} data-testid="csps-contraintes" className="mt-1" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-3 border-t">
            <p className="text-xs font-semibold text-[#FF6B00] uppercase tracking-wide mb-3">2. Donnees administratives</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="csps-numop">Numéro d'operation</Label>
                  <Input id="csps-numop" value={formData.numéro_operation}
                    onChange={(e) => setFormData({ ...formData, numéro_operation: e.target.value })}
                    placeholder="Ex: OP-2026-0042"
                    data-testid="csps-numéro-operation" className="mt-1 h-14" />
                </div>
                <div>
                  <Label htmlFor="csps-refdossier">Reference dossier</Label>
                  <Input id="csps-refdossier" value={formData.reference_dossier}
                    onChange={(e) => setFormData({ ...formData, reference_dossier: e.target.value })}
                    placeholder="Ex: CSPS-2026-LYN-012"
                    data-testid="csps-reference-dossier" className="mt-1 h-14" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-14">
              Annuler
            </Button>
            <Button type="submit" disabled={submitting}
              className="flex-1 h-14 bg-[#FF6B00] hover:bg-orange-700" data-testid="csps-chantier-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingChantier ? 'Enregistrér' : 'Créer')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
